/**
 * UFs, municipios e consulta de CEP.
 *
 * Os municipios vem do IBGE e o endereco do ViaCEP: dois servicos publicos, sem
 * chave e sem cadastro. Nenhum dos dois participa de salvar o formulario — sao
 * conveniencia de preenchimento. Por isso toda falha aqui e silenciosa para o
 * fluxo: quem chama decide o que fazer, e o padrao e deixar o usuario digitar
 * na mao. Um cadastro de empresa nao pode ficar refem da disponibilidade de um
 * servico de terceiro.
 */

export interface Uf {
  sigla: string;
  nome: string;
}

/** Fixa no codigo de proposito: sao 27 e nao mudam. Buscar isso na rede seria
 *  uma chamada a mais para uma lista que o Brasil nao altera ha decadas. */
export const UFS: Uf[] = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' },
];

const IBGE = 'https://servicodados.ibge.gov.br/api/v1/localidades/estados';
const VIACEP = 'https://viacep.com.br/ws';

// Sao Paulo tem 645 municipios e a lista nunca muda durante uma sessao. Guardar
// por UF evita repetir a busca quando o usuario troca de estado e volta.
const cacheMunicipios = new Map<string, string[]>();

/**
 * Municipios de uma UF, em ordem alfabetica.
 *
 * Devolve lista vazia quando a busca falha — cabe a quem chama distinguir
 * "ainda carregando" de "nao deu para carregar" pelo estado de erro.
 */
export async function buscarMunicipios(uf: string): Promise<string[]> {
  const sigla = uf.trim().toUpperCase();
  if (!sigla) return [];

  const emCache = cacheMunicipios.get(sigla);
  if (emCache) return emCache;

  const resposta = await fetch(`${IBGE}/${sigla}/municipios?orderBy=nome`);
  if (!resposta.ok) {
    throw new Error(`IBGE respondeu ${resposta.status}`);
  }

  const dados: Array<{ nome: string }> = await resposta.json();
  const nomes = dados.map((m) => m.nome);
  cacheMunicipios.set(sigla, nomes);
  return nomes;
}

export interface EnderecoDoCep {
  cep: string;
  logradouro: string;
  bairro: string;
  cidade: string;
  uf: string;
}

/**
 * Endereco de um CEP, ou null quando o CEP nao existe.
 *
 * O ViaCEP responde 200 mesmo para CEP inexistente, sinalizando com um campo
 * `erro` no corpo — e ja devolveu esse campo ora como booleano, ora como a
 * string "true". Testar so por verdade cobre os dois formatos.
 */
export async function buscarCep(cep: string): Promise<EnderecoDoCep | null> {
  const digitos = cep.replace(/\D/g, '');
  if (digitos.length !== 8) return null;

  const resposta = await fetch(`${VIACEP}/${digitos}/json/`);
  if (!resposta.ok) {
    throw new Error(`ViaCEP respondeu ${resposta.status}`);
  }

  const dados = await resposta.json();
  if (dados.erro) return null;

  return {
    cep: digitos,
    logradouro: dados.logradouro ?? '',
    bairro: dados.bairro ?? '',
    cidade: dados.localidade ?? '',
    uf: dados.uf ?? '',
  };
}
